import React from 'react';
import { Box, Typography, Paper, Button } from '@mui/material';
import { styled } from '@mui/material/styles';

interface ButtonOption {
  label: string;
  onClick: () => void;
  color?: string;
}

interface FeatureCardProps {
  title: string;
  description: string;
  icon?: React.ReactNode;
  onClick?: () => void;
  extraButtons?: ButtonOption[];
}

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  backgroundColor: 'rgba(26, 26, 46, 0.7)',
  backdropFilter: 'blur(10px)',
  borderRadius: theme.spacing(2),
  border: '1px solid rgba(138, 43, 226, 0.2)',
  height: '100%',
  transition: 'all 0.3s ease',
  cursor: 'pointer',
  '&:hover': {
    background: 'linear-gradient(90deg, #003366, #00CC99, #8A2BE2, #FF6F00)',
    backgroundSize: '300% 100%',
    animation: 'gradient 6s linear infinite',
    transform: 'translateY(-2px)',
    boxShadow: '0 10px 30px rgba(138, 43, 226, 0.3)',
    border: '1px solid rgba(138, 43, 226, 0.3)',
  },
}));

const IconContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 50,
  height: 50,
  borderRadius: '50%',
  background: 'linear-gradient(135deg, #8A2BE2 0%, #00CC99 100%)',
  marginBottom: theme.spacing(2),
  color: '#fff',
}));

const FeatureCard: React.FC<FeatureCardProps> = ({ 
  title, 
  description, 
  icon, 
  onClick,
  extraButtons 
}) => {
  // Stop event propagation for button clicks to prevent card click
  const handleButtonClick = (e: React.MouseEvent<HTMLButtonElement>, callback: () => void) => {
    e.stopPropagation();
    callback();
  };

  return (
    <StyledPaper onClick={onClick}>
      {icon && <IconContainer>{icon}</IconContainer>}
      
      <Typography 
        variant="h5" 
        sx={{ 
          color: '#fff',
          mb: 1,
          background: 'linear-gradient(90deg, #00CC99, #8A2BE2)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          display: 'inline-block'
        }}
      >
        {title}
      </Typography>
      
      <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)', mb: 2 }}>
        {description}
      </Typography>
      
      {extraButtons && extraButtons.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', mt: 2 }}>
          {extraButtons.map((button, index) => (
            <Button
              key={index}
              onClick={(e) => handleButtonClick(e, button.onClick)}
              sx={{
                mt: 1,
                mr: 1,
                bgcolor: 'rgba(138, 43, 226, 0.1)',
                color: button.color || '#00CC99',
                borderRadius: 4,
                px: 2,
                py: 0.5,
                fontSize: '0.8rem',
                transition: 'all 0.3s ease',
                '&:hover': {
                  bgcolor: 'rgba(138, 43, 226, 0.2)',
                  transform: 'translateY(-2px)',
                }
              }}
              variant="text"
              size="small"
            >
              {button.label}
            </Button>
          ))}
        </Box>
      )}
    </StyledPaper>
  );
};

export default FeatureCard; 